import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const FileUploadTriggerConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['upload', 'settings']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-3">
      {/* Fájl Feltöltés Beállítások */}
      <CollapsibleSection
        id="upload"
        title="Fájl Feltöltés Beállítások"
        icon="📁"
        color="blue"
        isExpanded={expandedSections.has('upload')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Engedélyezett Fájl Típusok
            </label>
            <input
              type="text"
              value={node.data.config.fileTypes || 'pdf,doc,docx,jpg,png'}
              onChange={(e) => updateConfig('fileTypes', e.target.value)}
              className={commonStyles.input}
              placeholder="pdf,doc,docx,jpg,png"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Vesszővel elválasztva, vagy "*" minden típushoz
            </p>
          </div>
          
          <div>
            <label className={commonStyles.label}>
              Maximum fájl méret (MB)
            </label>
            <input
              type="number"
              value={node.data.config.maxSizeMB || 10}
              onChange={(e) => updateConfig('maxSizeMB', parseInt(e.target.value))}
              className={commonStyles.input}
              placeholder="10"
              min="1"
              max="100"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Maximum engedélyezett fájl méret megabájtban
            </p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoProcess"
              checked={node.data.config.autoProcess !== false}
              onChange={(e) => updateConfig('autoProcess', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="autoProcess" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Automatikus feldolgozás
            </label>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3">
            <div className="flex items-start space-x-2">
              <span className="text-green-600 dark:text-green-400 text-sm">✅</span>
              <div className="text-xs text-green-700 dark:text-green-300">
                <strong>Felhasználóbarát megoldás:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Felhasználók közvetlenül feltölthetnek fájlokat</li>
                  <li>Automatikus validáció (típus, méret)</li>
                  <li>Azonnali flow indítás feltöltés után</li>
                  <li>Nincs szükség backend konfigurációra</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Elérhető Változók */}
      <CollapsibleSection
        id="variables"
        title="Elérhető Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A fájl feltöltés trigger után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{file_name}}', desc: 'Fájl neve', example: 'document.pdf' },
              { var: '{{file_url}}', desc: 'Fájl URL', example: 'https://storage.../document.pdf' },
              { var: '{{file_size}}', desc: 'Fájl méret (byte)', example: '1048576' },
              { var: '{{file_size_mb}}', desc: 'Fájl méret (MB)', example: '1.5' },
              { var: '{{file_extension}}', desc: 'Fájl kiterjesztés', example: 'pdf' },
              { var: '{{file_type}}', desc: 'MIME típus', example: 'application/pdf' },
              { var: '{{file_upload_time}}', desc: 'Feltöltés időpontja', example: '2024-01-01T12:00:00Z' },
              { var: '{{uploader_id}}', desc: 'Feltöltő felhasználó ID', example: 'user123' },
              { var: '{{auto_process}}', desc: 'Automatikus feldolgozás', example: 'true' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mt-3">
            <div className="text-xs text-blue-700 dark:text-blue-300">
              <strong>Használati példák:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><strong>Számla feldolgozás:</strong> PDF feltöltés → AI Text Analyzer → CRM</li>
                <li><strong>Képek feldolgozása:</strong> Kép feltöltés → AI Image Generator → Tárolás</li>
                <li><strong>Dokumentum workflow:</strong> Fájl feltöltés → CRM Task → Email értesítés</li>
                <li><strong>Automatikus kategorizálás:</strong> Fájl feltöltés → AI elemzés → Mappába rendezés</li>
              </ul>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Előnézet */}
      <CollapsibleSection
        id="preview"
        title="Konfiguráció Előnézet"
        icon="👁️"
        color="gray"
        isExpanded={expandedSections.has('preview')}
        onToggle={toggleSection}
      >
        <div className={commonStyles.codeBlock}>
          <div className="text-green-400 font-mono text-xs space-y-1">
            <div>📁 Fájl típusok: {node.data.config.fileTypes || 'pdf,doc,docx,jpg,png'}</div>
            <div>📏 Max méret: {node.data.config.maxSizeMB || 10} MB</div>
            <div>⚡ Automatikus: {node.data.config.autoProcess !== false ? 'igen' : 'nem'}</div>
            <div>🔗 Változók: 9 db elérhető a flow-ban</div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default FileUploadTriggerConfig;
